import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Home, Wrench, Plus, Clock, Search, Camera, ChevronLeft } from "lucide-react";
import { HoursPanel } from "@/components/HoursPanel";
import { PlateLookup } from "@/components/PlateLookup";
import { JobsList } from "@/components/BodyshopJobsList";

const NEW = "/admin/carrozzeria/nuova";
const TITLES = { jobs: "Lavori", hours: "Ore operatori", search: "Cerca targa o cliente" };

const Tile = ({ icon: I, title, sub, color, onClick, to, badge, testid }) => {
  const body = (
    <>
      <span className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5" style={{ background: `${color}22`, color }}><I className="w-6 h-6" /></span>
      <span className="block font-display font-bold uppercase text-lg leading-tight">{title}</span>
      <span className="block text-sm text-muted-foreground mt-1">{sub}</span>
      {badge > 0 && <span className="absolute top-4 right-4 min-w-[28px] h-7 px-2 rounded-full text-sm font-bold flex items-center justify-center text-white" style={{ background: color }} data-testid={`${testid}-badge`}>{badge}</span>}
    </>
  );
  const cls = "relative text-left rounded-2xl border border-white/10 bg-[#181A22] p-5 sm:p-6 hover:border-white/30 hover:bg-[#1E212B] active:scale-[0.98] transition-[transform,background-color,border-color] duration-200";
  return to ? <Link to={to} className={cls} data-testid={testid}>{body}</Link> : <button onClick={onClick} className={cls} data-testid={testid}>{body}</button>;
};

const HomeTiles = ({ go, open, orphans }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="bodyshop-home">
    <Tile icon={Plus} title="Nuova scheda" sub="Accetta un veicolo" color="#E63946" to={NEW} testid="home-tile-new" />
    <Tile icon={Camera} title="Carica ore" sub="Foto della scheda lavoro" color="#FF9E00" onClick={() => go("hours")} badge={orphans} testid="home-tile-hours" />
    <Tile icon={Search} title="Cerca" sub="Per targa o cliente" color="#3B82F6" onClick={() => go("search")} testid="home-tile-search" />
    <Tile icon={Wrench} title="Lavori aperti" sub="Riparazioni in corso" color="#10B981" onClick={() => go("jobs")} badge={open} testid="home-tile-jobs" />
  </div>
);

const NAV = [["home", "Home", Home], ["jobs", "Lavori", Wrench], ["new", "Nuova", Plus], ["hours", "Ore", Clock], ["search", "Cerca", Search]];

const BottomNav = ({ view, go, orphans }) => (
  <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[#181A22]/95 backdrop-blur-md border-t border-white/10 px-2 pt-1.5 pb-[max(0.5rem,env(safe-area-inset-bottom))] grid grid-cols-5" data-testid="bodyshop-bottom-nav">
    {NAV.map(([k, label, I]) => k === "new" ? (
      <Link key={k} to={NEW} className="flex flex-col items-center -mt-6" data-testid="bottom-nav-new" aria-label="Nuova scheda">
        <span className="w-14 h-14 rounded-full bg-[#E63946] text-white flex items-center justify-center shadow-lg shadow-[#E63946]/30 border-4 border-[#12141A]"><Plus className="w-6 h-6" /></span>
        <span className="text-[10px] mt-0.5 text-white">{label}</span>
      </Link>
    ) : (
      <button key={k} onClick={() => go(k)} className={`relative flex flex-col items-center gap-0.5 py-1 text-[10px] transition-colors ${view === k ? "text-white" : "text-muted-foreground"}`} data-testid={`bottom-nav-${k}`}>
        <I className={`w-5 h-5 ${view === k ? "text-[#E63946]" : ""}`} />{label}
        {k === "hours" && orphans > 0 && <span className="absolute top-0.5 right-[calc(50%-14px)] w-2.5 h-2.5 rounded-full bg-[#E63946]" data-testid="bodyshop-orphan-dot" />}
      </button>
    ))}
  </nav>
);

const TopNav = ({ view, go, orphans }) => (
  <div className="hidden md:flex items-center gap-1 rounded-full border border-white/10 bg-[#12141A] p-1 mb-6 w-fit" data-testid="bodyshop-top-nav">
    {NAV.filter(([k]) => k !== "new").map(([k, label, I]) => (
      <button key={k} onClick={() => go(k)} data-testid={`bodyshop-view-${k}`}
        className={`relative flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm transition-colors ${view === k ? "bg-white text-black font-semibold" : "text-muted-foreground hover:text-white"}`}>
        <I className="w-4 h-4" />{label}
        {k === "hours" && orphans > 0 && <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#E63946] ring-2 ring-[#12141A]" data-testid="bodyshop-orphan-dot-top" />}
      </button>
    ))}
    <Link to={NEW} className="ml-2 flex items-center gap-1.5 rounded-full bg-[#E63946] hover:bg-[#D62828] text-white px-4 py-1.5 text-sm font-semibold" data-testid="bodyshop-new-btn"><Plus className="w-4 h-4" />Nuova scheda</Link>
  </div>
);

export const BodyshopTab = () => {
  const [sp, setSp] = useSearchParams();
  const view = sp.get("v") || "home";
  const go = (v) => { setSp({ tab: "bodyshop", ...(v === "home" ? {} : { v }) }); window.scrollTo({ top: 0 }); };
  const [orphans, setOrphans] = useState(0);
  const [open, setOpen] = useState(0);
  const refresh = () => {
    api.get("/admin/timesheets/summary").then((r) => setOrphans(r.data.vehicles.filter((v) => !v.job).length)).catch(() => {});
    api.get("/admin/bodyshop").then((r) => setOpen(r.data.filter((j) => j.status !== "closed").length)).catch(() => {});
  };
  useEffect(refresh, [view]);

  return (
    <div className="pb-24 md:pb-0" data-testid="bodyshop-section">
      <TopNav view={view} go={go} orphans={orphans} />
      {view !== "home" && (
        <div className="flex items-center gap-2 mb-4">
          <button onClick={() => go("home")} className="flex items-center gap-1 rounded-full border border-white/15 bg-[#212530] hover:bg-[#2A2E3D] pl-2 pr-3 h-9 text-sm transition-colors" aria-label="Torna alla Carrozzeria" data-testid="bodyshop-back-home"><ChevronLeft className="w-5 h-5" />Indietro</button>
          <h2 className="font-display font-bold uppercase text-base">{TITLES[view]}</h2>
        </div>
      )}
      {view === "home" && <HomeTiles go={go} open={open} orphans={orphans} />}
      {view === "jobs" && <JobsList />}
      {view === "hours" && <HoursPanel onChange={refresh} />}
      {view === "search" && <PlateLookup autoFocus />}
      <BottomNav view={view} go={go} orphans={orphans} />
    </div>
  );
};
